/**
 * AI 상세 이미지 — 상세페이지용 긴 세로 이미지 렌더러 (React 없음).
 *
 * 방식: AI 는 글(제목·포인트·일차 요약)과 사진 선택만 하고, 그림은 이 모듈이 고정 HTML 템플릿(detailTemplate.js)에 채워 넣어
 * html2canvas 로 그린다. 한국어 글자는 브라우저 폰트가 그리므로 AI 이미지 생성처럼 글자가 깨질 일이 없고,
 * 같은 글로 테마·강조색만 바꿔 다시 그리는 데 비용이 들지 않는다.
 *
 * 이 파일이 맡는 브라우저 쪽 일:
 *   · inlineImage        — 사진을 data URL 로 (html2canvas 의 CORS 문제 회피)
 *   · extractAccentColor — 히어로 사진에서 강조색 뽑기 (accent 'auto')
 *   · renderRouteMapImage — 소개영상의 경로 지도(routeMap.js)를 정지 이미지로
 *   · drawBadge/swapBadges — DAY·POINT 알약을 캔버스 이미지로 (html2canvas 글자 위치 오차 회피)
 *   · renderDetailImage  — html2canvas 로 섹션별 조각(slice) 래스터라이즈, stitchSlices 로 한 장 합치기(다운로드용)
 */

import html2canvas from '../../utils/html2canvasLight';
import { loadImage } from '../video/introVideoBuilder';
import { buildRouteMap, drawRouteMap } from '../video/routeMap';
import { DETAIL_WIDTH, themeBackground } from './detailTemplate';

export { DETAIL_WIDTH, DETAIL_THEMES, DETAIL_ACCENTS, themeBackground, esc, buildDetailHtml } from './detailTemplate';

/**
 * 세리프 제목 폰트(warm 테마)는 앱이 싣지 않으므로 렌더 직전에 Google Fonts 에서 한 번만 불러온다.
 * 안 하면 PC 마다 다른 대체 폰트(맥은 고딕)가 찍혀 저장된 이미지가 만든 사람 컴퓨터에 따라 달라진다.
 */
const SERIF_FONT_URL = 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@600;700;800&display=swap';
const SERIF_FACES = ["600 23px 'Noto Serif KR'", "700 30px 'Noto Serif KR'", "800 56px 'Noto Serif KR'"];
async function ensureSerifFont() {
  if (!document.querySelector(`link[href="${SERIF_FONT_URL}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = SERIF_FONT_URL;
    document.head.appendChild(link);
  }
  if (!document.fonts?.load) return;
  // 폰트 서버가 느려도 렌더를 막지 않게 5초까지만 기다린다
  await Promise.race([
    Promise.all(SERIF_FACES.map((f) => document.fonts.load(f, '여행'))).catch(() => {}),
    new Promise((r) => setTimeout(r, 5000)),
  ]);
}

/**
 * 템플릿이 쓰는 본문 폰트 굵기를 미리 실어 둔다. Pretendard 는 굵기마다 파일이 따로라 화면에서 안 쓰던 굵기(800 등)는
 * 템플릿을 붙이는 순간 처음 요청된다. 그 사이에 html2canvas 가 대체 폰트 메트릭으로 글자 위치를 잡고 글자만 Pretendard 로 그리면
 * 알약 안의 글자가 위아래로 밀린다(실제로 그랬다). 5초 넘게 걸리면 그냥 진행한다.
 */
const BODY_FACES = [500, 600, 700, 800].map((w) => `${w} 16px 'Pretendard'`);
async function ensureBodyFonts(doc = document) {
  if (!doc.fonts?.load) return;
  // 굵기별 파일이 1MB 안팎이라 첫 로드는 느릴 수 있다. 15초까지 기다리고 넘긴다
  await Promise.race([
    Promise.all(BODY_FACES.map((f) => doc.fonts.load(f, '여행 DAY 01'))).catch(() => {}),
    new Promise((r) => setTimeout(r, 15000)),
  ]);
  if (doc.fonts.ready) await doc.fonts.ready.catch(() => {});
}

/**
 * html2canvas 는 문서를 iframe 에 **복제**하고, 글자 위치는 그 복제본의 레이아웃(Range 사각형)으로 재되 글자는 원본 창의 폰트로 그린다.
 * 복제본은 새 문서라 폰트가 다시 실리는데, html2canvas 가 기다리는 fonts.ready 는 레이아웃 전이라 아직 아무것도 기다릴 게 없어 바로 지나간다.
 * 그래서 복제본에서 레이아웃을 한 번 강제한 뒤 폰트를 기다린다 — 안 그러면 대체 폰트 자리에 Pretendard 글자가 찍혀 알약 안에서 위로 뜬다.
 */
async function waitCloneFonts(doc, theme) {
  void doc.body?.offsetHeight;
  await ensureBodyFonts(doc);
  if (theme === 'warm' && doc.fonts?.load) {
    await Promise.race([
      Promise.all(SERIF_FACES.map((f) => doc.fonts.load(f, '여행'))).catch(() => {}),
      new Promise((r) => setTimeout(r, 5000)),
    ]);
  }
}

/**
 * 이미지 URL(또는 data URL)을 canvas 로 다시 그려 data URL 로 만든다.
 * html2canvas 는 다른 출처의 <img> 를 그릴 때 CORS 응답이 없으면 빈칸으로 두는데, S3 캐시 문제(loadImage 주석 참고)로
 * 실패가 잦다. 그래서 렌더 전에 사진을 전부 data URL 로 인라인해 둔다. 실패하면 null.
 * @param {Map<string, ImageBitmap|null>} [cache]  loadImage 캐시 (url → bitmap)
 * @param {number} [maxWidth]  긴 변 상한 — 히어로가 900px 폭 × 2배율 = 1800px 로 그려지므로 그 이상은 필요 없다
 */
export async function inlineImage(url, cache, maxWidth = DETAIL_WIDTH * 2) {
  if (!url) return null;
  if (/^data:/i.test(url)) return url;
  const img = await loadImage(url, cache);
  if (!img) return null;
  const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
  if (!iw || !ih) return null;
  const s = Math.min(1, maxWidth / Math.max(iw, ih));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(iw * s));
  c.height = Math.max(1, Math.round(ih * s));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  try {
    return c.toDataURL('image/jpeg', 0.9);
  } catch {
    return null; // tainted canvas (crossOrigin 없이 로드된 폴백 이미지)
  }
}

// ── 강조색 자동 추출 ─────────────────────────────────────────────

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h / 6, s, l };
}
function hslToHex(h, s, l) {
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * 히어로 사진에서 강조색을 뽑는다. 채도가 있는 픽셀만 모아 색상(hue)의 평균을 내고,
 * 글자·버튼에 쓸 수 있게 채도·명도를 일정 범위로 눌러 준다(하늘색 사진이면 짙은 파랑, 노을이면 진한 주황).
 * 채도 있는 픽셀이 너무 적으면(흑백·눈밭) null — 호출 쪽이 기본색을 쓴다.
 * @returns {Promise<string|null>} '#rrggbb'
 */
export async function extractAccentColor(dataUrl) {
  if (!dataUrl) return null;
  const img = await new Promise((resolve) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = dataUrl;
  });
  if (!img) return null;
  const N = 48;
  const c = document.createElement('canvas');
  c.width = N; c.height = N;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0, N, N);
  const { data } = ctx.getImageData(0, 0, N, N);
  let x = 0, y = 0, wSum = 0, sSum = 0, lSum = 0, count = 0;
  for (let i = 0; i < data.length; i += 4) {
    const { h, s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2]);
    if (s < 0.22 || l < 0.12 || l > 0.85) continue;
    const w = s * s; // 채도가 높을수록 그 색이 사진의 인상을 결정한다
    x += Math.cos(h * 2 * Math.PI) * w;
    y += Math.sin(h * 2 * Math.PI) * w;
    wSum += w; sSum += s * w; lSum += l * w; count++;
  }
  if (count < N * N * 0.04 || !wSum) return null;
  let h = Math.atan2(y, x) / (2 * Math.PI);
  if (h < 0) h += 1;
  const s = Math.max(0.5, Math.min(0.78, sSum / wSum + 0.15));
  const l = Math.max(0.3, Math.min(0.42, lSum / wSum - 0.12));
  return hslToHex(h, s, l);
}

// ── 경로 지도 ────────────────────────────────────────────────────

/** 지도 칸 크기(CSS px, 템플릿 routeSection 과 같게) × 2 배율 */
const MAP_W = 772 * 2;
const MAP_H = 480 * 2;

/**
 * 소개영상의 경로 지도(Google Static Maps 다크 스타일 + 경로선·지명)를 정지 이미지로 그린다.
 * 애니메이션 끝 시점(t=1)을 한 장 찍는 것이라 점·선·라벨이 전부 보인다. 키가 없거나 지도를 못 받으면 추상 약도.
 * @returns {Promise<string|null>} PNG data URL. 경로가 2곳 미만이면 null
 */
export async function renderRouteMapImage({ route, googleMapsKey, imageCache }) {
  const map = await buildRouteMap({ route, width: MAP_W, height: MAP_H, googleMapsKey, imageCache, loadImage });
  if (!map) return null;
  const c = document.createElement('canvas');
  c.width = MAP_W; c.height = MAP_H;
  const ctx = c.getContext('2d');
  drawRouteMap(ctx, { layout: 'map', mode: 'full', start: 0, end: 1, map }, 1, MAP_W, MAP_H, 1);
  try {
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

// ── 뱃지(알약) 이미지 ────────────────────────────────────────────

/**
 * 알약 라벨을 캔버스로 그려 data URL 로. textBaseline='middle' 이라 어떤 폰트가 실려 있든 글자가 정확히 가운데 온다 —
 * html2canvas 의 글자 위치 계산(폰트 메트릭 추정)에 기대지 않으려는 것. 자간은 ctx.letterSpacing(Chrome 99+), 없으면 글자별로 찍는다.
 * @returns {{ url: string, w: number, h: number }|null}  w/h 는 CSS px
 */
export function drawBadge({ text, h, px, fs, ls = 0, weight = 800, bg, color, font }, scale = 2) {
  try {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    const fontStr = `${weight} ${fs * scale}px ${font}`;
    const lsPx = Math.round(fs * ls * scale);
    const chars = [...String(text)];
    ctx.font = fontStr;
    const hasLS = 'letterSpacing' in ctx;
    let textW;
    if (hasLS) { ctx.letterSpacing = `${lsPx}px`; textW = ctx.measureText(text).width - lsPx; } // 마지막 글자 뒤 자간은 뺀다
    else textW = chars.reduce((w, ch) => w + ctx.measureText(ch).width, 0) + lsPx * (chars.length - 1);
    const W = Math.round(textW + px * 2 * scale);
    const H = Math.round(h * scale);
    c.width = W; c.height = H;
    // 캔버스 크기를 바꾸면 상태가 초기화되므로 다시 설정
    ctx.font = fontStr;
    if (hasLS) ctx.letterSpacing = `${lsPx}px`;
    ctx.fillStyle = bg;
    ctx.beginPath();
    const r = H / 2;
    ctx.moveTo(r, 0); ctx.arcTo(W, 0, W, H, r); ctx.arcTo(W, H, 0, H, r); ctx.arcTo(0, H, 0, 0, r); ctx.arcTo(0, 0, W, 0, r); ctx.closePath();
    ctx.fill();
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const y = H / 2 + Math.round(scale * 0.5); // 대문자·숫자만 있는 라벨은 시각적으로 살짝 위에 보여 반 픽셀 내린다
    if (hasLS) ctx.fillText(text, px * scale, y);
    else { let x = px * scale; for (const ch of chars) { ctx.fillText(ch, x, y); x += ctx.measureText(ch).width + lsPx; } }
    return { url: c.toDataURL('image/png'), w: W / scale, h: H / scale };
  } catch {
    return null;
  }
}

/** 템플릿의 data-badge 알약을 캔버스 이미지로 바꿔치기한다 (폰트가 실린 뒤에 불러야 한다). 실패한 것은 HTML 알약 그대로 둔다. */
function swapBadges(root, scale) {
  for (const el of root.querySelectorAll('[data-badge]')) {
    let spec = null;
    try { spec = JSON.parse(el.getAttribute('data-badge')); } catch { /* 형식이 다르면 HTML 알약 유지 */ }
    if (!spec) continue;
    const img = drawBadge(spec, scale);
    if (!img) continue;
    el.textContent = '';
    el.style.cssText = `display:inline-block;width:${img.w}px;height:${img.h}px;background-image:url(${img.url});background-size:100% 100%;background-repeat:no-repeat;vertical-align:middle;`;
    el.removeAttribute('data-badge');
  }
}

// ── 렌더 ────────────────────────────────────────────────────────

const abortError = () => new DOMException('중단됨', 'AbortError');

/** 조각 하나의 CSS 높이 상한. 이보다 크면 다음 섹션을 새 조각으로 시작한다. ×2 배율이면 8000px — 캔버스 상한(16384) 안에서 넉넉하다. */
const SLICE_MAX_CSS_HEIGHT = 4000;

/**
 * 템플릿을 화면 밖 숨은 컨테이너에 붙이고 html2canvas 로 그린다.
 * 숨기는 방법이 display:none 이 아니라 화면 밖(left:-20000px)인 이유: display:none 이면 레이아웃이 없어 아무것도 안 그려진다.
 *
 * 한 장으로 그리지 않고 **조각(slice)** 으로 나눈다: 루트의 직계 <section> 마다 따로 그려서 SLICE_MAX_CSS_HEIGHT 안에서 이어 붙인다.
 * 이유: 일차마다 사진 카드가 들어가면 전체가 CSS 1만 px 을 넘고, ×2 배율이면 캔버스 한 변 상한(16384)을 넘어 배율을 깎아야 했다(흐릿해짐).
 * 쇼핑몰 상세 이미지가 원래 여러 장으로 올라가는 이유이기도 하다 — 첨부할 때는 조각들을 <img> 로 세로로 이어 붙인다.
 * @param {(p:{ stage:string, done:number, total:number }) => void} [onProgress]
 * @returns {Promise<{ slices: Array<{ blob: Blob, width: number, height: number }>, width: number, height: number, mimeType: string, scale: number }>}
 *   width/height 는 전체(조각 합산) 픽셀 크기
 */
export async function renderDetailImage({ html, theme = 'modern', format = 'jpeg', quality = 0.92, scale = 2, signal, onProgress }) {
  if (signal?.aborted) throw abortError();
  const div = document.createElement('div');
  div.style.cssText = `position:fixed;left:-20000px;top:0;width:${DETAIL_WIDTH}px;z-index:-1;pointer-events:none;`;
  div.innerHTML = html;
  document.body.appendChild(div);
  try {
    // 레이아웃을 한 번 강제해 브라우저가 필요한 폰트 파일 요청을 시작하게 한 뒤 폰트를 기다린다
    void div.offsetHeight;
    await ensureBodyFonts();
    if (theme === 'warm') await ensureSerifFont();
    if (document.fonts?.ready) await document.fonts.ready.catch(() => {});
    if (signal?.aborted) throw abortError();

    const root = div.firstElementChild || div;
    // 알약 라벨은 폰트가 실린 지금 캔버스 이미지로 바꾼다 (글자 위치가 html2canvas 계산에 안 흔들리게)
    swapBadges(root, scale);
    const rootTop = root.getBoundingClientRect().top;
    const rootHeight = root.offsetHeight;
    // 섹션의 실제 위치로 조각을 나눈다. 높이를 offsetHeight 로 더해 가면 섹션 사이로 빠져나간 margin 이 빠져 다음 조각이 앞 조각을 덮는다.
    const els = [...root.children].filter((el) => el.offsetHeight > 0);
    const sections = els.map((el, i) => {
      const top = el.getBoundingClientRect().top - rootTop;
      const end = i + 1 < els.length ? els[i + 1].getBoundingClientRect().top - rootTop : rootHeight;
      return { el, top, height: el.offsetHeight, span: end - top };
    });
    if (!sections.length) throw new Error('그릴 내용이 없습니다.');
    const bg = themeBackground(theme);
    const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';

    // 섹션 → 조각 묶기 (연속 섹션을 높이 상한 안에서 합친다)
    const groups = [];
    for (const sc of sections) {
      const last = groups[groups.length - 1];
      if (last && last.height + sc.span <= SLICE_MAX_CSS_HEIGHT) { last.items.push(sc); last.height += sc.span; } else groups.push({ items: [sc], top: sc.top, height: sc.span });
    }

    const slices = [];
    let total = 0;
    let done = 0;
    onProgress?.({ stage: 'render', done: 0, total: sections.length });
    for (const g of groups) {
      // 조각 캔버스: 섹션들을 차례로 그려 넣는다
      const out = document.createElement('canvas');
      out.width = Math.round(DETAIL_WIDTH * scale);
      out.height = Math.round(g.height * scale);
      const octx = out.getContext('2d');
      octx.fillStyle = bg;
      octx.fillRect(0, 0, out.width, out.height);
      for (const sc of g.items) {
        const canvas = await html2canvas(sc.el, {
          scale,
          useCORS: true,
          backgroundColor: bg,
          logging: false,
          windowWidth: DETAIL_WIDTH,
          width: DETAIL_WIDTH,
          height: sc.height,
          // html2canvas 는 문서 전체를 iframe 에 복제해 놓고 그린다. 상세설정 화면은 수천 노드 + 미리보기 iframe 이라
          // 그대로 두면 렌더마다 화면 전체를 복제하고 미리보기 페이지까지 다시 받는다. 템플릿과 그 조상(html/body), <head> 만 남긴다.
          ignoreElements: (e) => !(e === div || div.contains(e) || e.contains(div) || e.closest?.('head')),
          onclone: (doc) => waitCloneFonts(doc, theme),
        });
        if (signal?.aborted) throw abortError();
        octx.drawImage(canvas, 0, Math.round((sc.top - g.top) * scale), out.width, Math.round(sc.height * scale));
        done++;
        onProgress?.({ stage: 'render', done, total: sections.length });
      }
      const blob = await new Promise((resolve) => out.toBlob(resolve, mimeType, mimeType === 'image/jpeg' ? quality : undefined));
      if (!blob) throw new Error('이미지를 만들지 못했습니다. PNG 대신 JPEG 를 쓰거나 섹션을 줄여 보세요.');
      slices.push({ blob, width: out.width, height: out.height });
      total += out.height;
    }
    return { slices, width: Math.round(DETAIL_WIDTH * scale), height: total, mimeType, scale };
  } finally {
    div.remove();
  }
}

/**
 * 조각들을 세로로 이어 한 장으로 만든다(다운로드용). 캔버스 한 변 상한을 넘으면 null — 호출 쪽이 조각별로 내려받게 한다.
 */
export async function stitchSlices(slices, mimeType = 'image/jpeg', quality = 0.92) {
  const width = slices[0]?.width || 0;
  const height = slices.reduce((n, s) => n + s.height, 0);
  if (!width || !height || height > 16000) return null;
  const c = document.createElement('canvas');
  c.width = width; c.height = height;
  const ctx = c.getContext('2d');
  let y = 0;
  for (const s of slices) {
    const bmp = await createImageBitmap(s.blob);
    ctx.drawImage(bmp, 0, y);
    bmp.close?.();
    y += s.height;
  }
  const blob = await new Promise((resolve) => c.toBlob(resolve, mimeType, mimeType === 'image/jpeg' ? quality : undefined));
  return blob ? { blob, width, height } : null;
}
